import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticlePoutineVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "vancouver-poutine-guide";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "La Belle Patate",
    "section-2": "Belgian Fries",
    "section-3": "Fritz European Fry House",
    "section-4": "Mean Poutine",
    "section-5": "The Templeton",
    "section-6": "MeeT (Multiple Locations)",
    "section-7": "The Flying Pig",
    "section-8": "Steamworks Brewpub",
    "section-9": "Fable Diner",
    "section-10": "Smoke’s Poutinerie",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.tourismvancouver.com/",
      label: "Tourism Vancouver (Official Site)",
    },
    {
      url: "https://www.hellobc.com/plan-your-trip/",
      label: "Hello BC – Plan Your Trip",
    },
    {
      text: "La Poutine Week (Annual Poutine Festival). Retrieved 2025.",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    {
      id: "section-1",
      lat: 49.28203,
      lng: -123.13355,
      popupText: "La Belle Patate (West End)",
    },
    {
      id: "section-2",
      lat: 49.27000,
      lng: -123.06900,
      popupText: "Belgian Fries (Commercial Drive)",
    },
    {
      id: "section-3",
      lat: 49.27952,
      lng: -123.12700,
      popupText: "Fritz European Fry House (Downtown)",
    },
    {
      id: "section-4",
      lat: 49.27900,
      lng: -123.12100,
      popupText: "Mean Poutine (Granville Entertainment District)",
    },
    {
      id: "section-5",
      lat: 49.27800,
      lng: -123.12600,
      popupText: "The Templeton (Downtown Diner)",
    },
    {
      id: "section-6",
      lat: 49.24860,
      lng: -123.10100,
      popupText: "MeeT on Main (Mount Pleasant)",
    },
    {
      id: "section-7",
      lat: 49.27670,
      lng: -123.11900,
      popupText: "The Flying Pig (Yaletown)",
    },
    {
      id: "section-8",
      lat: 49.28550,
      lng: -123.11199,
      popupText: "Steamworks Brewpub (Gastown)",
    },
    {
      id: "section-9",
      lat: 49.26250,
      lng: -123.09800,
      popupText: "Fable Diner (Mount Pleasant)",
    },
    {
      id: "section-10",
      lat: 49.27920,
      lng: -123.12180,
      popupText: "Smoke’s Poutinerie (Downtown)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Vancouver Poutine Guide: 10 Must-Try Spots</title>
        <meta
          name="description"
          content="Discover Vancouver's best poutine: from Quebec-style classics to late-night favorites, plus vegan, upscale twists, and more."
        />
        <meta
          name="keywords"
          content="poutine, Vancouver, best poutine, food, restaurants"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/vancouver-poutine-guide.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Vancouver Poutine Guide: 10 Must-Try Spots"
        />
        <meta
          property="og:description"
          content="A thorough look at where to find Vancouver's top poutine joints, from classic diners to vegan picks."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/vancouver-poutine-guide.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/vancouver-poutine-guide.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Vancouver Poutine Guide: 10 Must-Try Spots"
        />
        <meta
          name="twitter:description"
          content="Explore the top places to enjoy poutine in Vancouver, including addresses, coordinates, and practical tips."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/vancouver-poutine-guide.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Classic Quebec-Style Poutine with cheese curds and gravy"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver may be famous for its fresh seafood and sushi, but
              there's another indulgence you can't miss here:
              <strong> poutine</strong>. This iconic blend of crispy fries,
              squeaky cheese curds, and rich gravy hails from Quebec, yet
              Vancouver has fully embraced it, offering everything from classic
              renditions to creative fusions. Whether you're a local craving
              comfort food or a tourist seeking a true Canadian dish, this guide
              highlights ten must-try poutine hotspots across the city.
            </p>

            {/* SECTION 1: La Belle Patate */}
            <h2 id="section-1">La Belle Patate (West End)</h2>
            <p
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.28203}
                coordX={-123.13355}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              1215 Davie St, Vancouver, BC V6E 1N4, Canada
            </p>
            <p>
              <strong>Hours:</strong> Tue 11:00–18:00; Wed/Thu 11:00–23:00; Fri
              11:00–00:00; Sat 12:00–00:00; Sun 13:00–19:00 (closed Monday).
              <br />
              <strong>Approx. Cost:</strong> ~$9–$15 (generous portions).
            </p>
            <p>
              Serving authentic Quebec-style poutine with squeaky cheese curds
              and gravy imported straight from Quebec, La Belle Patate is a
              casual spot beloved by purists. The menu brims with over 40
              varieties, ranging from a classic version to smoked-meat or
              perogy-topped options. Expect a laid-back vibe perfect for
              indulging in hearty comfort food.
            </p>

            {/* SECTION 2: Belgian Fries */}
            <h2 id="section-2">Belgian Fries (Commercial Drive)</h2>
            <p
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.27}
                coordX={-123.069}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              1885 Commercial Dr, Vancouver, BC V5N 4A6, Canada
            </p>
            <p>
              <strong>Hours:</strong> Mon/Tue 11:30–21:00; Wed/Thu/Sun
              11:30–22:00; Fri/Sat 11:30–23:00.
              <br />
              <strong>Approx. Cost:</strong> $8–$14 for poutines (various
              sizes).
            </p>
            <p>
              Belgian Fries is an East Vancouver staple that, despite the name,
              offers standout Canadian-style poutine with vegetarian-friendly
              gravy. Hand-cut fries, savory toppings like pulled pork or butter
              chicken, and a mellow Commercial Drive atmosphere combine for a
              delightful (and filling) experience.
            </p>

            {/* SECTION 3: Fritz European Fry House */}
            <h2 id="section-3">Fritz European Fry House (Downtown)</h2>
            <p
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.27952}
                coordX={-123.127}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              718 Davie St, Vancouver, BC V6Z 1B6, Canada
            </p>
            <p>
              <strong>Hours:</strong> Tue–Wed 17:30–02:30; Thu 11:30–03:30; Fri
              11:30–04:00; Sat 13:00–04:00; Sun 13:00–02:00 (closed Mondays).
              <br />
              <strong>Approx. Cost:</strong> $5.50–$12.50 depending on
              size/toppings.
            </p>
            <p>
              A legendary late-night pit stop, Fritz is famed for its fresh-cut
              fries, signature gravy, and extensive topping selections. Try
              smoked meat, pulled pork, or vegetarian cheese and gravy for a
              customized feast. It's a small space, so don’t be surprised by
              lineups on weekends, but fans swear it’s worth the wait.
            </p>

            {/* SECTION 4: Mean Poutine */}
            <h2 id="section-4">Mean Poutine (Granville Entertainment District)</h2>
            <p
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.279}
                coordX={-123.121}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              718 Nelson St, Vancouver, BC V6Z 1A8, Canada
            </p>
            <p>
              <strong>Hours:</strong> Mon–Thu 11:00–23:00; Fri–Sat 11:00–04:00;
              Sun closed.
              <br />
              <strong>Approx. Cost:</strong> $8–$12; combos available.
            </p>
            <p>
              Mean Poutine balances traditional Quebec-style with West Coast
              flair. Try the butter chicken or BBQ pulled pork variations, or
              build your own. The location is perfect for quick bites along
              Granville’s bustling nightlife strip, making it a favorite with
              bar hoppers.
            </p>

            {/* SECTION 5: The Templeton */}
            <h2 id="section-5">The Templeton (Downtown Diner)</h2>
            <p
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.278}
                coordX={-123.126}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              1087 Granville St, Vancouver, BC V6Z 1L4, Canada
            </p>
            <p>
              <strong>Hours:</strong> Mon–Wed 08:30–15:00; Thu 08:30–21:00; Fri
              08:30–22:00; Sat 08:30–22:00; Sun 08:30–21:00.
              <br />
              <strong>Approx. Cost:</strong> ~$8–$12 for poutine.
            </p>
            <p>
              A retro diner dating back to the 1940s, The Templeton serves
              no-frills poutine that's as classic as it gets—golden fries, fresh
              cheese curds, and a creamy gravy. The atmosphere, complete with
              jukeboxes and retro décor, adds to its timeless charm. Perfect for
              brunch lovers or those seeking a late-night comfort meal.
            </p>

            {/* SECTION 6: MeeT (Multiple Locations) */}
            <h2 id="section-6">MeeT on Main, Gastown, & Yaletown (Vegan/Vegetarian)</h2>
            <p
              ref={(el) => setSpotRef("section-6", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>MeeT on Main:</strong>{" "}
              <Marker
                coordY={49.2486}
                coordX={-123.101}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              4288 Main St, Vancouver, BC V5V 3P9
            </p>
            <p>
              <strong>Hours:</strong> Daily ~11:00–22:00 (varies by location).
              <br />
              <strong>Approx. Cost:</strong> $12–$15 (fully plant-based).
            </p>
            <p>
              MeeT is famous for making comfort food accessible to vegetarians
              and vegans, including a stellar plant-based poutine with vegan
              cheese and gravy. Their Butter Chikkin Poutine is a highlight, but
              each location also offers varied mains, desserts, and craft beers
              in a cheerful setting.
            </p>

            {/* SECTION 7: The Flying Pig */}
            <h2 id="section-7">The Flying Pig (Yaletown & Olympic Village)</h2>
            <p
              ref={(el) => setSpotRef("section-7", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location (Yaletown):</strong>{" "}
              <Marker
                coordY={49.2767}
                coordX={-123.119}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              1168 Hamilton St, Vancouver, BC V6B 2S2
            </p>
            <p>
              <strong>Hours (Yaletown):</strong> Mon–Wed 15:00–23:00; Thu
              11:30–23:00; Fri 11:30–00:00; Sat 11:30–00:00; Sun 11:30–22:00.
              <br />
              <strong>Approx. Cost:</strong> ~$14 for poutine appetizer.
            </p>
            <p>
              Known for refined West Coast bistro fare, The Flying Pig’s
              signature pulled pork poutine is a decadent appetizer. Served in a
              stylish setting, it’s perfect for those who want a more upscale
              dining experience without sacrificing indulgent Canadian
              comfort-food vibes.
            </p>

            {/* SECTION 8: Steamworks Brewpub */}
            <h2 id="section-8">Steamworks Brewpub (Gastown)</h2>
            <p
              ref={(el) => setSpotRef("section-8", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2855}
                coordX={-123.11199}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              375 Water St, Vancouver, BC V6B 1B8, Canada
            </p>
            <p>
              <strong>Hours:</strong> Mon–Thu ~11:30–24:00; Fri 11:30–01:00; Sat
              11:00–01:00; Sun 11:00–24:00.
              <br />
              <strong>Approx. Cost:</strong> ~$12 for poutine (add pulled pork
              for extra).
            </p>
            <p>
              Steamworks is popular for craft beers and pub fare in historic
              Gastown. Their crispy fries, gooey curds, and velvety gravy make
              for a satisfying poutine. Grab a seat near the steam clock and
              enjoy the lively atmosphere—an excellent spot to refuel after
              exploring the neighborhood.
            </p>

            {/* SECTION 9: Fable Diner */}
            <h2 id="section-9">Fable Diner (Mount Pleasant)</h2>
            <p
              ref={(el) => setSpotRef("section-9", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2625}
                coordX={-123.098}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              151 E Broadway, Vancouver, BC V5T 1W1, Canada
            </p>
            <p>
              <strong>Hours:</strong> Mon–Sun 09:00–21:00 (brunch until 14:30 on
              weekends).
              <br />
              <strong>Approx. Cost:</strong> “Cafeteria Poutine” side ~$6 (with
              meal).
            </p>
            <p>
              A modern diner concept from the minds behind Fable Kitchen, Fable
              Diner is known for using fresh, local ingredients. Though poutine
              is often served as a side option here, the quality is top-notch.
              Tater tots or fries, squeaky curds, and a luscious gravy make it a
              hidden gem for poutine enthusiasts.
            </p>

            {/* SECTION 10: Smoke’s Poutinerie */}
            <h2 id="section-10">Smoke’s Poutinerie (Downtown)</h2>
            <p
              ref={(el) => setSpotRef("section-10", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2792}
                coordX={-123.1218}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              942 Granville St, Vancouver, BC V6Z 1L2, Canada
            </p>
            <p>
              <strong>Hours:</strong> Sun–Thu 12:00–22:00; Fri–Sat 12:00–03:00.
              <br />
              <strong>Approx. Cost:</strong> ~$8–$12 with unlimited topping
              combos.
            </p>
            <p>
              A well-known Canadian chain, Smoke’s Poutinerie offers an entire
              menu of creative poutine variations, from triple pork to butter
              chicken or veggie chili. Quick service, extended weekend hours,
              and a cheeky plaid aesthetic make this a fun downtown option for
              both locals and visitors.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. What is poutine, and why is it so popular in Canada?</h3>
              <p>
                Poutine is a comforting combination of fries, cheese curds, and
                gravy that originated in Quebec. Canadians love it for the
                irresistible contrast of crispy fries and melty curds in savory
                gravy. It’s considered by many to be an unofficial national
                dish.
              </p>

              <h3>2. Are there vegetarian or vegan poutine options in Vancouver?</h3>
              <p>
                Absolutely. Many places (like MeeT and Fritz) offer vegetarian
                gravy and even vegan cheese. Vancouver’s diverse dining scene
                ensures that plant-based poutine lovers won’t miss out on the
                fun.
              </p>

              <h3>3. How much should I expect to pay for poutine in Vancouver?</h3>
              <p>
                Basic poutines range from about $6–$10 for a small, while
                specialty versions or larger servings can go up to $15. In
                general, it’s an affordable way to enjoy a hearty meal.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              Vancouver’s restaurants are regulated by Vancouver Coastal Health,
              requiring all to maintain high standards of food safety and
              hygiene. They undergo routine health inspections to ensure
              compliance with local guidelines. If you have dietary concerns or
              allergies, most restaurants can accommodate you—just ask in
              advance.
            </p>
            <p>
              As for COVID-19 or other health-related measures, always check
              official Vancouver or BC government websites for current dining
              guidelines or mandates. Rules can change, so stay informed to have
              a safe and enjoyable experience.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              From late-night greasy spoons to upscale bistros and vegan diners,
              Vancouver’s poutine scene has something for everyone. Whether
              you’re a die-hard traditionalist or eager to try innovative
              toppings, these ten spots represent the city’s best takes on
              Canada’s unofficial national dish. Follow your cravings, bring a
              friend to share (or not!), and get ready for a deliciously cheesy
              adventure.
            </p>
            <p>
              <strong>Disclaimer:</strong> Always double-check hours and current
              regulations, especially if you have dietary restrictions. All info
              provided is for guidance only; contact restaurants directly for
              the latest updates.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}